// ====================================================================================
// COMPONENT: CurrentCampaigns (Fixed: Casing & Context Passing)
// Self-contained with MiniGrade, fetch logic, simulation cards, and all icons.
// ====================================================================================
import { useState, useEffect } from "react";
import { encryptedFetch as fetch } from "../../security";
import {
  Brain,
  Loader2,
  AlertTriangle,
  Plus,
  Calendar,
  Target,
  MapPin,
  Timer,
  ArrowRight,
} from "lucide-react";

// --- Sub-component: MiniGrade (Glass Variant) ---
const MiniGrade = ({ grade }) => {
  const config = {
    'A+': { text: 'text-teal-300', bg: 'bg-teal-500/10', border: 'border-teal-500/20' },
    'A':  { text: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
    'B':  { text: 'text-blue-300', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
    'C':  { text: 'text-yellow-300', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
    'D':  { text: 'text-orange-300', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
    'F':  { text: 'text-red-300', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  }[grade] || { text: 'text-slate-400', bg: 'bg-slate-500/5', border: 'border-slate-500/10' };

  return (
    <div className={`flex flex-col items-center justify-center w-8 h-8 rounded-md border ${config.border} ${config.bg} backdrop-blur-md shadow-[inset_0_0_10px_rgba(0,0,0,0.2)]`}>
      <span className={`text-sm font-bold ${config.text} leading-none font-sans`}>{grade}</span>
    </div>
  );
};

// --- Helper: Date Parsing ---
const parseDate = (timestamp) => {
  if (!timestamp) return new Date(0);
  if (timestamp.toDate) return timestamp.toDate();
  if (typeof timestamp === 'string') return new Date(timestamp);
  return new Date(0);
};

const CurrentCampaigns = ({ campaignId, setActiveView }) => {
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch Logic
  useEffect(() => {
    if (!campaignId) return;

    const fetchSimulations = async () => {
      setLoading(true);
      try {
        const response = await fetch("https://us-central1-knoesis-dante.cloudfunctions.net/getSimulatedCampaign?campaignID=" + campaignId);
        const data = await response.json();

        let processed = Array.isArray(data) ? data : [];
        processed.sort((a, b) => {
          const dateA = parseDate(a.analysis_metadata?.timestamp);
          const dateB = parseDate(b.analysis_metadata?.timestamp);
          return dateB - dateA;
        });

        setSimulations(processed.slice(0, 10));
        setLoading(false);
      } catch (err) {
        console.error("Error loading campaigns:", err);
        setError("Failed to sync simulation data.");
        setLoading(false);
      }
    };

    fetchSimulations();
  }, [campaignId]);

  const formatDateStr = (timestamp) => {
    const dateObj = parseDate(timestamp);
    return dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-4 sm:p-6 relative flex flex-col space-y-6 overflow-hidden">

      {/* --- Header Section --- */}
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight">Matrix Campaign Centre</h2>
          <p className="text-xs text-slate-400 font-medium mt-1 uppercase tracking-wider">Your saved simulations</p>
        </div>

        {/* Enter The Matrix Button */}
        <button
          onClick={() => setActiveView('plato')}
          className="flex items-center gap-2 py-2.5 px-6 rounded-lg text-sm font-bold text-slate-100
                  bg-slate-700 hover:bg-slate-600
                  border border-transparent
                  shadow-[0_0_15px_rgba(255,255,255,0.15)]
                  hover:shadow-[0_0_25px_rgba(255,255,255,0.4)]
                  active:bg-gradient-to-br active:from-slate-700 active:to-slate-800
                  transition-all duration-300 uppercase tracking-widest whitespace-nowrap"
        >
          <Brain size={16} className="text-indigo-200 group-hover:text-white transition-colors" />
          <span>Enter The Matrix</span>
        </button>
      </div>

      {/* --- Horizontal Scroll Container --- */}
      <div className="relative min-h-[240px]">

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mr-2" size={20} /> <span className="text-xs font-bold uppercase tracking-widest">Loading Archives...</span>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-rose-400 bg-rose-500/5 border border-rose-500/20 rounded-xl">
            <AlertTriangle className="mr-2" size={20} /> <span className="text-xs font-bold uppercase tracking-widest">{error}</span>
          </div>
        )}

        {!loading && !error && (
          <div className="flex overflow-x-auto space-x-4 pb-4 snap-x custom-scrollbar">

            {/* Empty State / Start New Card */}
            {simulations.length === 0 && (
              <div
                onClick={() => setActiveView('plato')}
                className="min-w-[280px] h-[220px] rounded-xl border border-dashed border-slate-700 bg-slate-900/30 hover:bg-slate-800/60 hover:border-slate-500 cursor-pointer flex flex-col items-center justify-center text-center transition-all duration-300 snap-start group"
              >
                <div className="w-12 h-12 rounded-full bg-slate-900 border border-slate-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Plus size={20} className="text-slate-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-bold text-slate-300 text-sm group-hover:text-white transition-colors">Start New Campaign</h3>
                <p className="text-[10px] text-slate-500 mt-1 px-6 uppercase tracking-wide">Begin a new simulation</p>
              </div>
            )}

            {/* Simulation Cards */}
            {simulations.map((sim, index) => {
              const summary = sim.input_summary || {};
              const scorecard = sim.performance_dashboard?.campaign_scorecard || {};
              const optimization = sim.campaign_optimization?.optimal_budget_allocation || {};
              const metadata = sim.analysis_metadata || {};

              const dateStr = formatDateStr(metadata.timestamp);
              const projectedVotes = optimization.expected_total_votes || 0;
              const wardCount = summary.ward_count || 0;

              return (
                <div
                  key={index}
                  onClick={() => setActiveView('campaignGallery', sim)}
                  className="group min-w-[300px] relative flex flex-col justify-between h-[220px] bg-slate-900/60 backdrop-blur-sm rounded-xl p-5 cursor-pointer border border-slate-700/50 hover:border-slate-500/50 hover:bg-slate-800/60 hover:shadow-xl transition-all duration-300 snap-start"
                >
                  {/* Card Header */}
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-500 bg-slate-950/50 px-2 py-1 rounded border border-slate-800">
                        <Calendar size={10} />
                        <span>{dateStr}</span>
                      </div>
                      <MiniGrade grade={scorecard.overall_grade || '-'} />
                    </div>

                    <h3 className="font-bold text-white text-base leading-snug line-clamp-2 mb-1 group-hover:text-indigo-300 transition-colors">
                      {summary.campaign_name || 'Untitled Campaign'}
                    </h3>

                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Target size={12} className="text-slate-500" />
                      <span className="truncate max-w-[200px] font-medium text-slate-500">{summary.campaign_focus?.replace(/_/g, ' ') || 'General Strategy'}</span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent my-1"></div>

                  {/* KPI Grid */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Budget</div>
                        <div className="text-emerald-400 font-mono font-bold text-sm">
                          £{(summary.budget || 0).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-0.5 flex items-center justify-end gap-1">
                          Votes
                        </div>
                        <div className="text-blue-400 font-mono font-bold text-sm">
                          {Math.round(projectedVotes).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {/* Footer Stats */}
                    <div className="flex justify-between items-center pt-1 border-t border-slate-800/50 mt-1">
                      <div className="flex items-center gap-3 text-[9px] text-slate-500 font-bold uppercase tracking-wider">
                        <div className="flex items-center gap-1" title="Wards">
                          <MapPin size={10} /> {wardCount} Wards
                        </div>
                        <div className="flex items-center gap-1" title="Days">
                          <Timer size={10} /> {summary.days_until_election || 0}d
                        </div>
                      </div>
                      <div className="w-6 h-6 rounded-full bg-slate-950 flex items-center justify-center border border-slate-800 group-hover:border-white/20 group-hover:bg-slate-800 transition-all">
                        <ArrowRight size={12} className="text-slate-600 group-hover:text-white transition-colors" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* --- Footer Action --- */}
      <div className="flex justify-end">
        <button
          onClick={() => setActiveView('campaignGallery')}
          className="text-[10px] font-bold text-slate-500 hover:text-white flex items-center gap-1 transition-colors uppercase tracking-widest group"
        >
          View Full Library <ArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
};

export default CurrentCampaigns;
